import React from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Image,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTint,
  faWind,
  faUmbrella,
  faLocation,
} from "@fortawesome/free-solid-svg-icons";

const WeatherMetric = ({ icon, label, value, unit }) => (
  <Col className="text-center">
    <FontAwesomeIcon icon={icon} size="1x" color="white" />
    <p>
      {label}: {value}
      {unit}
    </p>
  </Col>
);

const WeatherMsg = ({
  temperature,
  temp_f,
  conditions,
  humidity,
  wind_speed,
  location,
  precip_in,
  summary,
  pm2_5,
  pm10,
  condition_icon,
}) => {
  const cardStyle = {
    // backgroundImage:
    //   "linear-gradient(to top, #260f39 0%, #190f3e 1%, #152a4c 100%)",
    backgroundColor: "rgba(40, 105, 148, 0.2)",
    backdropFilter: "blur(5px)",
    color: "#d9d9d9",
    maxWidth: "400px",
    border: "none",
    margin: "20px 0",
  };

  const footerStyle = {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "white",
  };

  const weatherMetrics = [
    {
      icon: faTint,
      label: "Humidity",
      value: humidity,
      unit: "%",
    },
    {
      icon: faWind,
      label: "Wind",
      value: wind_speed,
      unit: "km/h",
    },
    {
      icon: faUmbrella,
      label: "Precipitation (in)",
      value: precip_in,
      unit: "in",
    },
  ];
  
  const AQIVisualization = ({ pm25 = 0, pm10 = 0 }) => {
    const ranges = [
      { min: 0, max: 50, color: '#00e400', label: 'Good' },
      { min: 51, max: 100, color: '#ffff00', label: 'Moderate' },
      { min: 101, max: 150, color: '#ff7e00', label: 'Unhealthy for Sensitive Groups' },
      { min: 151, max: 200, color: '#ff0000', label: 'Unhealthy' }
    ];
  
    const gradient = ranges.map(range => `${range.color}`).join(', ');
  
    const getPosition = (value) => {
      const totalRange = ranges[ranges.length - 1].max - ranges[0].min;
      return Math.min(Math.max((value / totalRange) * 100, 0), 100);
    };
  
    const pm25Position = getPosition(pm25);
    const pm10Position = getPosition(pm10);
  
    return (
        <Card className="" style={{color: "#d9d9d9", maxWidth: '400px', 
          backgroundColor: "rgba(40, 105, 148, 0.2)",
          backdropFilter: "blur(5px)",

        }}>
          <Card.Header>
            <Card.Title className="mb-0" style={{
              fontSize: "1em"
            }}>Avg. Air Quality Index (AQI)</Card.Title>
          </Card.Header>
          <Card.Body className="px-4 py-3">
            {/* PM2.5 Visualization */}
            <div className="mb-4">
              <div className="text-gray-300 mb-2">PM2.5</div><br />
              <div className="position-relative" style={{ height: '32px' }}>
                {/* Dark background for contrast */}
                <div 
                  className="position-absolute w-100 rounded-pill"
                  style={{ height: '8px', backgroundColor: '#1a1a1a' }}
                />
                {/* Gradient bar */}
                <div 
                  className="position-absolute w-100 rounded-pill"
                  style={{
                    height: '8px',
                    background: `linear-gradient(to right, ${gradient})`,
                    opacity: 0.9
                  }}
                />
                {/* Pointer */}
                <div 
                  className="position-absolute rounded-circle shadow"
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#60a5fa',
                    border: '2px solid #000',
                    left: `${pm25Position}%`,
                    transform: 'translate(-50%, -25%)',
                    transition: 'left 0.3s ease-in-out'
                  }}
                />
                {/* Value label */}
                <div 
                  className="position-absolute text-gray-300"
                  style={{
                    left: `${pm25Position}%`,
                    top: '-24px',
                    transform: 'translateX(-50%)',
                    fontSize: '0.875rem'
                  }}
                >
                  {pm25}
                </div>
              </div>
            </div>
  
            {/* PM10 Visualization */}
            <div className="mb-4">
              <div className="text-gray-300 mb-2">PM10</div><br />
              <div className="position-relative" style={{ height: '32px' }}>
                {/* Dark background for contrast */}
                <div 
                  className="position-absolute w-100 rounded-pill"
                  style={{ height: '8px', backgroundColor: '#1a1a1a' }}
                />
                {/* Gradient bar */}
                <div 
                  className="position-absolute w-100 rounded-pill"
                  style={{
                    height: '8px',
                    background: `linear-gradient(to right, ${gradient})`,
                    opacity: 0.9
                  }}
                />
                {/* Pointer */}
                <div 
                  className="position-absolute rounded-circle shadow"
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: '#60a5fa',
                    border: '2px solid #000',
                    left: `${pm10Position}%`,
                    transform: 'translate(-50%, -25%)',
                    transition: 'left 0.3s ease-in-out'
                  }}
                />
                {/* Value label */}
                <div 
                  className="position-absolute text-gray-300"
                  style={{
                    left: `${pm10Position}%`,
                    top: '-24px',
                    transform: 'translateX(-50%)',
                    fontSize: '0.875rem'
                  }}
                >
                  {pm10}
                </div>
              </div>
            </div>
  
            {/* Legend */}
            <div className="d-flex justify-content-between mt-4">
              {ranges.map((range) => (
                <div 
                  key={range.label}
                  className="text-center"
                  style={{ fontSize: '0.75rem' }}
                >
                  <div 
                    className="rounded-circle mx-auto mb-1"
                    style={{ 
                      width: '16px', 
                      height: '16px', 
                      backgroundColor: range.color 
                    }}
                  />
                  <div className="text-gray-300">{range.label}</div>
                  <div className="text-gray-400">{`${range.min}-${range.max}`}</div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
    );
  };
  
  return (
    <>
      <p>{summary}</p>
      <Card className="shadow-lg" style={cardStyle}>
        <Card.Body>
          <Row className="text-center mb-3">
            <Col>
              <h1 className="mt-2">{(location.indexOf("United States of America") > -1 || location.indexOf("USA") > -1)? `${temp_f} °F`: `${temperature} °C`}</h1>
              <p><Image style={{
                maxWidth: "40px"
              }} src={condition_icon} /> {conditions}</p>
            </Col>
          </Row>

          <Row>
            {weatherMetrics.map((metric, index) => (
              <WeatherMetric
                key={index}
                icon={metric.icon}
                label={metric.label}
                value={metric.value}
                unit={metric.unit}
              />
            ))}
          </Row>
          <Card.Footer className="text-center" style={footerStyle}>
            <h6>
              <FontAwesomeIcon icon={faLocation} /> {location}
            </h6>
          </Card.Footer>
        </Card.Body>
      </Card>
      <Row style={{
        marginBottom: "20px"
      }}>
        <Container fluid style={{ color: "#d9d9d9" }}>
          <AQIVisualization pm10={pm10} pm25={pm2_5} />
        </Container>
      </Row>
    </>
  );
};

export default WeatherMsg;
